/**
 * Liturgical Calendar Utility
 * Calculates the current Catholic liturgical season and returns appropriate colors
 */

// Calculate Easter Sunday using the Anonymous Gregorian algorithm
function calculateEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// Get the date of Ash Wednesday (46 days before Easter)
function getAshWednesday(year) {
  const easter = calculateEaster(year);
  const ashWednesday = new Date(easter);
  ashWednesday.setDate(easter.getDate() - 46);
  return ashWednesday;
}

// Get Pentecost Sunday (50 days after Easter)
function getPentecost(year) {
  const easter = calculateEaster(year);
  const pentecost = new Date(easter);
  pentecost.setDate(easter.getDate() + 49);
  return pentecost;
}

// Get first Sunday of Advent (4th Sunday before Christmas)
function getAdventStart(year) {
  const christmas = new Date(year, 11, 25); // Dec 25
  const dayOfWeek = christmas.getDay(); // 0 = Sunday
  // Find the 4th Sunday before Christmas
  const daysToSubtract = dayOfWeek === 0 ? 28 : (dayOfWeek + 21);
  const advent = new Date(christmas);
  advent.setDate(christmas.getDate() - daysToSubtract);
  return advent;
}

// Get Baptism of the Lord (Sunday after Jan 6, or Jan 7 if Jan 6 is Sunday)
function getBaptismOfLord(year) {
  const epiphany = new Date(year, 0, 6); // Jan 6
  if (epiphany.getDay() === 0) {
    // Epiphany is Sunday, Baptism is next day (Monday Jan 7)
    return new Date(year, 0, 7);
  }
  // Find next Sunday after Jan 6
  const daysUntilSunday = 7 - epiphany.getDay();
  const baptism = new Date(epiphany);
  baptism.setDate(epiphany.getDate() + daysUntilSunday);
  return baptism;
}

// Liturgical seasons with their colors
export const LITURGICAL_SEASONS = {
  ADVENT: {
    name: 'Advent',
    description: 'Preparing for the coming of Christ',
    colors: {
      primary: '#6B3FA0',      // Royal Purple
      primaryHover: '#5A2D8C',
      primaryLight: '#8B5FBF',
      secondary: '#D4AF37',    // Gold accent
      secondaryHover: '#C4A030',
      accent: '#9B59B6',
      text: '#4A2272',
      muted: '#E8DFF0',
      ring: '#6B3FA0',
    }
  },
  CHRISTMAS: {
    name: 'Christmas',
    description: 'Celebrating the birth of Christ',
    colors: {
      primary: '#B8860B',      // Dark Gold - readable with white text
      primaryHover: '#A07608',
      primaryLight: '#D4AF37',
      secondary: '#D4AF37',    // Gold accent (not white)
      secondaryHover: '#C4A030',
      accent: '#DAA520',
      text: '#7A6400',
      muted: '#FFF8E7',
      ring: '#B8860B',
    }
  },
  ORDINARY_TIME: {
    name: 'Ordinary Time',
    description: 'Growing in faith through the year',
    colors: {
      primary: '#2E7D32',      // Liturgical Green
      primaryHover: '#256D27',
      primaryLight: '#4CAF50',
      secondary: '#D4AF37',    // Gold accent
      secondaryHover: '#C4A030',
      accent: '#388E3C',
      text: '#1B5E20',
      muted: '#E8F5E9',
      ring: '#2E7D32',
    }
  },
  LENT: {
    name: 'Lent',
    description: 'A season of penance and preparation',
    colors: {
      primary: '#6B3FA0',      // Penitential Purple
      primaryHover: '#5A2D8C',
      primaryLight: '#8B5FBF',
      secondary: '#8E44AD',
      secondaryHover: '#7D3C98',
      accent: '#9B59B6',
      text: '#4A2272',
      muted: '#E8DFF0',
      ring: '#6B3FA0',
    }
  },
  EASTER: {
    name: 'Easter',
    description: 'Rejoicing in the Resurrection',
    colors: {
      primary: '#B8860B',      // Dark Gold - readable with white text
      primaryHover: '#A07608',
      primaryLight: '#D4AF37',
      secondary: '#D4AF37',    // Gold accent (not white)
      secondaryHover: '#C4A030',
      accent: '#DAA520',
      text: '#7A6400',
      muted: '#FFF8E7',
      ring: '#B8860B',
    }
  },
  PENTECOST: {
    name: 'Pentecost',
    description: 'The descent of the Holy Spirit',
    colors: {
      primary: '#C62828',      // Fiery Red
      primaryHover: '#B71C1C',
      primaryLight: '#EF5350',
      secondary: '#D4AF37',    // Gold
      secondaryHover: '#C4A030',
      accent: '#E53935',
      text: '#8B0000',
      muted: '#FFEBEE',
      ring: '#C62828',
    }
  }
};

/**
 * Determine the current liturgical season
 * @param {Date} date - The date to check (defaults to today)
 * @returns {Object} - The liturgical season object with name, description, and colors
 */
export function getLiturgicalSeason(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Create date without time for comparison
  const today = new Date(year, month, day);
  
  // Calculate key dates for current and previous year
  const adventStart = getAdventStart(year);
  const christmas = new Date(year, 11, 25);
  const baptismOfLord = getBaptismOfLord(year);
  const ashWednesday = getAshWednesday(year);
  const easter = calculateEaster(year);
  const pentecost = getPentecost(year);
  
  // Check for Advent (can span two years)
  const prevYearAdvent = getAdventStart(year - 1);
  if (today >= adventStart && today < christmas) {
    return LITURGICAL_SEASONS.ADVENT;
  }
  
  // Check for Christmas season (Dec 25 to Baptism of Lord)
  if ((today >= christmas) || (month === 0 && today < baptismOfLord)) {
    // Handle year transition
    const relevantBaptism = month === 0 ? baptismOfLord : getBaptismOfLord(year + 1);
    if (month === 0 && today < relevantBaptism) {
      return LITURGICAL_SEASONS.CHRISTMAS;
    }
    if (month === 11 && day >= 25) {
      return LITURGICAL_SEASONS.CHRISTMAS;
    }
  }
  
  // Check for Lent (Ash Wednesday to Holy Thursday evening)
  const holyThursday = new Date(easter);
  holyThursday.setDate(easter.getDate() - 3);
  if (today >= ashWednesday && today < holyThursday) {
    return LITURGICAL_SEASONS.LENT;
  }
  
  // Check for Easter Triduum and Easter Season
  if (today >= holyThursday && today < pentecost) {
    // Pentecost Sunday itself is red
    const pentecostDay = new Date(pentecost);
    if (today.getTime() === pentecostDay.getTime()) {
      return LITURGICAL_SEASONS.PENTECOST;
    }
    return LITURGICAL_SEASONS.EASTER;
  }
  
  // Check for Pentecost Sunday
  if (today.getTime() === pentecost.getTime()) {
    return LITURGICAL_SEASONS.PENTECOST;
  }
  
  // Default to Ordinary Time
  return LITURGICAL_SEASONS.ORDINARY_TIME;
}

/**
 * Get CSS custom properties for the current liturgical season
 * @param {Object} season - The liturgical season object
 * @returns {Object} - CSS custom properties object
 */
export function getSeasonCSSVars(season) {
  const { colors } = season;
  return {
    '--liturgical-primary': colors.primary,
    '--liturgical-primary-hover': colors.primaryHover,
    '--liturgical-primary-light': colors.primaryLight,
    '--liturgical-secondary': colors.secondary,
    '--liturgical-secondary-hover': colors.secondaryHover,
    '--liturgical-accent': colors.accent,
    '--liturgical-text': colors.text,
    '--liturgical-muted': colors.muted,
    '--liturgical-ring': colors.ring,
  };
}

/**
 * Get the logo path for the current liturgical season
 * Uses the dynamic logo API which generates logos with liturgical colors
 * @param {Object} season - The liturgical season object (optional, uses current if not provided)
 * @returns {string} - Path to the season-appropriate logo
 */
export function getSeasonLogo(season = null) {
  const currentSeason = season || getLiturgicalSeason();
  const v = 3; // bump to bust cache when logo changes
  
  switch (currentSeason.name) {
    case 'Advent':
    case 'Lent':
      return `/logos/parish-logo-purple.png?v=${v}`;
    case 'Christmas':
    case 'Easter':
      return `/logos/parish-logo-gold.png?v=${v}`;
    case 'Pentecost':
      return `/logos/parish-logo-red.png?v=${v}`;
    case 'Ordinary Time':
    default:
      return `/logos/parish-logo-green.png?v=${v}`;
  }
}

/**
 * Get the favicon path for the current liturgical season
 * @param {Object} season - The liturgical season object (optional, uses current if not provided)
 * @returns {string} - Path to the season-appropriate favicon
 */
export function getSeasonFavicon(season = null) {
  const currentSeason = season || getLiturgicalSeason();
  
  switch (currentSeason.name) {
    case 'Advent':
    case 'Lent':
      return '/favicon-purple.png';
    case 'Christmas':
    case 'Easter':
      return '/favicon-gold.png';
    case 'Pentecost':
      return '/favicon-red.png';
    case 'Ordinary Time':
    default:
      return '/favicon-green.png';
  }
}

/**
 * Update the favicon in the document head
 * @param {Object} season - The liturgical season object (optional)
 */
export function updateFavicon(season = null) {
  const faviconPath = getSeasonFavicon(season);
  
  // Update or create the favicon link element
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconPath;
  
  // Also update apple-touch-icon if present
  let appleLink = document.querySelector("link[rel='apple-touch-icon']");
  if (appleLink) {
    // Use the larger logo for apple touch icon
    appleLink.href = getSeasonLogo(season);
  }
}

/**
 * Format a date range for display
 * @param {Date} start 
 * @param {Date} end 
 * @returns {string}
 */
export function formatSeasonDates(start, end) {
  const options = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}

export default getLiturgicalSeason;
